/**
 * Phase 15 — Business-Payload aus entity_*-Stores + Blob-Hülle (nicht-entitäre Felder).
 */

import { AGGREGATE_TYPES } from './domainAggregateStore';
import { hasDiscrepancyManagementContent } from '../../utils/resolveDiscrepancyForEcarts';
import {
  getEntityStockData,
  isEntityStoreAuthorityEnabled,
  isEntityStoreEnabled,
  listEntityCustomers,
  listEntityLots,
  listEntityProducts,
  listEntityPurchases,
  listEntitySales,
  listEntitySuppliers,
  listEntityInvoices,
  listEntityOrders,
  listEntityExpenses,
  listEntityInvoicePayments,
  listEntitySupplierDebts,
  listEntityCustomerDebts,
  listEntityCustomerInvoices,
  listEntitySupermarketPayments,
  listEntitySupermarketInvoices,
  listEntityExpiredProducts,
  listEntityNotifications,
  listEntityWorkerSessions,
  getEntityReports,
  getEntityDiscrepancyManagement,
  getEntityStockLocations,
  listEntityPersistentStockData,
  listEntityOfflineQueue,
  getEntityBusinessInfo,
  getEntityUserInfo,
  getEntityMetadata,
  listEntityStockMovements
} from './entityAggregateStore';
import {
  businessDataStorageKey,
  ENTITY_PAYLOAD_FIELD_NAMES,
  isEntityShellBlob,
  normalizeBusinessPayloadShape,
  readBestBusinessMergedPayload,
  scoreBusinessMergedPayload,
  writeBusinessMergedPayload
} from '../../utils/sharedCore';

const ENTITY_PAYLOAD_FIELDS = ENTITY_PAYLOAD_FIELD_NAMES;

const ROOT_DOCUMENT_FIELDS = new Set([
  AGGREGATE_TYPES.STOCK_DATA,
  AGGREGATE_TYPES.REPORTS,
  AGGREGATE_TYPES.DISCREPANCY_MANAGEMENT,
  AGGREGATE_TYPES.BUSINESS_INFO,
  AGGREGATE_TYPES.USER_INFO,
  AGGREGATE_TYPES.METADATA
]);

/** Blob enthält entity-Felder nicht mehr (nur suppliers, stockData, …). */
export function isEntityStoreSkipBlobFieldsEnabled() {
  if (!isEntityStoreAuthorityEnabled()) return false;
  const v = process.env.REACT_APP_ENTITY_STORE_SKIP_BLOB;
  return v === 'true' || v === '1';
}

/** @type {Record<string, (businessId: string) => Promise<unknown>>} */
const ENTITY_FIELD_LOADERS = {
  sales: listEntitySales,
  purchases: listEntityPurchases,
  lots: listEntityLots,
  products: listEntityProducts,
  customers: listEntityCustomers,
  suppliers: listEntitySuppliers,
  stockData: getEntityStockData,
  invoices: listEntityInvoices,
  orders: listEntityOrders,
  expenses: listEntityExpenses,
  invoicePayments: listEntityInvoicePayments,
  supplierDebts: listEntitySupplierDebts,
  customerDebts: listEntityCustomerDebts,
  customerInvoices: listEntityCustomerInvoices,
  supermarketPayments: listEntitySupermarketPayments,
  supermarketInvoices: listEntitySupermarketInvoices,
  expiredProducts: listEntityExpiredProducts,
  notifications: listEntityNotifications,
  workerSessions: listEntityWorkerSessions,
  reports: getEntityReports,
  discrepancyManagement: getEntityDiscrepancyManagement,
  stockLocations: getEntityStockLocations,
  persistentStockData: listEntityPersistentStockData,
  offlineQueue: listEntityOfflineQueue,
  businessInfo: getEntityBusinessInfo,
  userInfo: getEntityUserInfo,
  metadata: getEntityMetadata,
  stockMovements: listEntityStockMovements
};

/**
 * @param {object} next
 * @param {string} field
 * @param {unknown} value
 */
function assignEntityFieldValue(next, field, value) {
  if (field === 'stockData' || field === 'reports' || field === 'metadata') {
    if (value && typeof value === 'object' && Object.keys(value).length > 0) {
      next[field] = value;
    }
    return;
  }
  if (field === 'discrepancyManagement') {
    if (hasDiscrepancyManagementContent(value)) {
      next.discrepancyManagement = value;
    }
    return;
  }
  if (field === 'businessInfo' || field === 'userInfo') {
    if (value) next[field] = value;
    return;
  }
  if (Array.isArray(value) && value.length > 0) {
    next[field] = value;
  }
}

/**
 * Fast path: nur benötigte entity_*-Felder laden (Edit/Save ohne Full-Assemble).
 * @param {string} businessId
 * @param {string[]} fields
 * @param {object} [base]
 */
export async function overlayEntityFieldsOnPayload(businessId, fields, base = {}) {
  if (!businessId || !isEntityStoreEnabled()) {
    return normalizeBusinessPayloadShape(base);
  }

  const wanted = [...new Set((fields || []).filter((f) => ENTITY_FIELD_LOADERS[f]))];
  if (wanted.length === 0) {
    return normalizeBusinessPayloadShape(base);
  }

  const loaded = await Promise.all(wanted.map((field) => ENTITY_FIELD_LOADERS[field](businessId)));
  const next = { ...base };
  wanted.forEach((field, i) => assignEntityFieldValue(next, field, loaded[i]));
  return normalizeBusinessPayloadShape(next);
}

/**
 * @param {string} businessId
 * @param {object} [base]
 */
export async function overlayEntityStoresOnPayload(businessId, base = {}) {
  return overlayEntityFieldsOnPayload(businessId, ENTITY_PAYLOAD_FIELDS, base);
}

/**
 * Non-entity fields that still live in a thin KV shell under NO_KV_BLOB
 * (entity_* has no table for these — without the shell they vanish on reload).
 */
export const NO_KV_BLOB_SHELL_FIELDS = [
  'restaurantDishes',
  'stockConsumptions',
  'restaurantTables',
  'openTickets',
  'drafts',
  'recipes',
  'productionOrders',
  // ADR-0016 ledger projection fields — no entity_* tables yet; live in thin shell
  'inventoryLedgerMeta',
  'stockMovementsArchive',
  'stockSnapshots'
];

/**
 * Merge thin-shell fields (openTickets, drafts, …) into a NO_KV_BLOB base payload.
 * @param {object} base
 * @param {object|null|undefined} shellParsed
 */
export function mergeNoKvBlobShellFields(base, shellParsed) {
  const next = base && typeof base === 'object' ? { ...base } : {};
  if (!shellParsed || typeof shellParsed !== 'object') return next;
  const shell = stripEntityFieldsForBlobSnapshot(shellParsed);
  for (const field of NO_KV_BLOB_SHELL_FIELDS) {
    if (shell[field] !== undefined) {
      next[field] = shell[field];
    }
  }
  return next;
}

/**
 * Kanonischer Lesepfad Phase 15: entity_* hat Vorrang für die fünf Aggregate-Felder.
 * @param {string} businessId
 */
export async function readBusinessPayloadWithEntityAuthority(businessId) {
  const { isEntityStoreNoKvBlobEnabled, readBusinessPayloadMeta } = await import(
    './businessPayloadMeta'
  );

  let base = {};
  if (isEntityStoreNoKvBlobEnabled()) {
    const meta = await readBusinessPayloadMeta(businessId);
    base = {
      payloadVersion: meta.payloadVersion,
      payloadUpdatedAt: meta.payloadUpdatedAt || undefined,
      entityStorePrimary: true,
      entityStoreNoKvBlob: true
    };
    // Thin shell still holds cuisine tickets / brouillons / tables (not in entity_*).
    try {
      const best = readBestBusinessMergedPayload(businessId);
      if (best?.parsed) {
        base = mergeNoKvBlobShellFields(base, best.parsed);
      }
    } catch {
      /* shell optional */
    }
  } else {
    const best = readBestBusinessMergedPayload(businessId);
    base = best.parsed ? normalizeBusinessPayloadShape(best.parsed) : {};
  }

  if (!isEntityStoreAuthorityEnabled()) {
    return base;
  }

  return overlayEntityStoresOnPayload(businessId, base);
}

/**
 * Vor Blob-Serialisierung: entity-Felder aus KV-Snapshot entfernen wenn SKIP_BLOB aktiv.
 * @param {object} payload
 */
export function stripEntityFieldsForBlobSnapshot(payload) {
  if (!isEntityStoreSkipBlobFieldsEnabled() || !payload) {
    return payload;
  }
  const next = { ...payload };
  for (const field of ENTITY_PAYLOAD_FIELDS) {
    delete next[field];
  }
  next.entityStorePrimary = true;
  return normalizeBusinessPayloadShape(next);
}

/**
 * @param {string} businessId
 */
export async function compareBlobWithEntityStores(businessId) {
  const { isEntityStoreNoKvBlobEnabled, readBusinessPayloadMeta } = await import(
    './businessPayloadMeta'
  );
  const assembled = await overlayEntityStoresOnPayload(businessId, {});
  const best = readBestBusinessMergedPayload(businessId);
  const blob = best.parsed || {};

  if (isEntityShellBlob(blob) && isEntityStoreSkipBlobFieldsEnabled()) {
    const diff = {};
    for (const field of ENTITY_PAYLOAD_FIELDS) {
      if (ROOT_DOCUMENT_FIELDS.has(field)) {
        const entityKeys = Object.keys(assembled[field] || {}).length;
        diff[field] = {
          blobCount: 0,
          entityCount: entityKeys,
          match: true,
          entityShell: true
        };
        continue;
      }
      const entityArr = Array.isArray(assembled[field]) ? assembled[field] : [];
      diff[field] = {
        blobCount: 0,
        entityCount: entityArr.length,
        match: true,
        entityShell: true
      };
    }
    return {
      businessId: String(businessId),
      blobKey: best.key,
      payloadVersion: blob.payloadVersion,
      allMatch: true,
      entityShell: true,
      diff
    };
  }

  if (isEntityStoreNoKvBlobEnabled()) {
    const meta = await readBusinessPayloadMeta(businessId);
    if (meta.noKvBlob) {
      const diff = {};
      for (const field of ENTITY_PAYLOAD_FIELDS) {
        if (ROOT_DOCUMENT_FIELDS.has(field)) {
          const entityKeys = Object.keys(assembled[field] || {}).length;
          diff[field] = {
            blobCount: 0,
            entityCount: entityKeys,
            match: true,
            noKvBlob: true
          };
          continue;
        }
        const entityArr = Array.isArray(assembled[field]) ? assembled[field] : [];
        diff[field] = {
          blobCount: 0,
          entityCount: entityArr.length,
          match: true,
          noKvBlob: true
        };
      }
      return {
        businessId: String(businessId),
        blobKey: null,
        payloadVersion: meta.payloadVersion,
        allMatch: true,
        noKvBlob: true,
        migrated: true,
        diff
      };
    }
  }

  const diff = {};
  for (const field of ENTITY_PAYLOAD_FIELDS) {
    if (ROOT_DOCUMENT_FIELDS.has(field)) {
      const blobKeys = Object.keys(blob[field] || {}).length;
      const entityKeys = Object.keys(assembled[field] || {}).length;
      diff[field] = {
        blobCount: blobKeys,
        entityCount: entityKeys,
        match: blobKeys === entityKeys
      };
      continue;
    }
    const blobArr = Array.isArray(blob[field])
      ? blob[field]
      : blob[field] && typeof blob[field] === 'object'
        ? Object.values(blob[field])
        : [];
    const entityArr = Array.isArray(assembled[field]) ? assembled[field] : [];
    diff[field] = {
      blobCount: blobArr.length,
      entityCount: entityArr.length,
      match: blobArr.length === entityArr.length
    };
  }

  const allMatch = Object.values(diff).every((entry) => entry.match);
  const entityTotal = Object.values(diff).reduce(
    (sum, entry) => sum + Number(entry?.entityCount || 0),
    0
  );
  const hasBlob = Boolean(best.parsed);
  const shell = isEntityShellBlob(blob);
  const viable =
    allMatch &&
    (entityTotal > 0 || shell || (hasBlob && scoreBusinessMergedPayload(blob) > 0));

  return {
    businessId: String(businessId),
    blobKey: best.key,
    payloadVersion: blob.payloadVersion,
    allMatch: viable,
    entityTotal,
    diff
  };
}

/**
 * Erzeugt Blob-Hülle aus vorhandenem Snapshot (payloadVersion bleibt erhalten).
 * @param {object} [previous]
 */
export function buildEntityShellBlob(previous = {}) {
  const shell = stripEntityFieldsForBlobSnapshot(previous);
  const prevVer = Number(previous?.payloadVersion);
  shell.payloadVersion = Number.isFinite(prevVer) && prevVer >= 0 ? prevVer : 1;
  shell.payloadUpdatedAt = new Date().toISOString();
  shell.entityStorePrimary = true;
  return normalizeBusinessPayloadShape(shell);
}

/**
 * Phase 21 — Voller Blob → reine Hülle, wenn entity_* mit Blob übereinstimmen.
 * @param {string} businessId
 */
export async function compressBlobToEntityShell(businessId) {
  if (!businessId) return { ok: false, error: 'missing businessId' };
  if (!isEntityStoreSkipBlobFieldsEnabled()) {
    return { ok: false, skipped: true, reason: 'skip_blob_disabled' };
  }

  const best = readBestBusinessMergedPayload(businessId);
  if (!best.parsed) {
    return { ok: false, skipped: true, reason: 'no_blob' };
  }
  if (isEntityShellBlob(best.parsed)) {
    return { ok: true, skipped: true, reason: 'already_shell', businessId: String(businessId) };
  }

  const comparison = await compareBlobWithEntityStores(businessId);
  if (!comparison.allMatch) {
    return {
      ok: false,
      skipped: true,
      reason: 'entity_mismatch',
      businessId: String(businessId),
      diff: comparison.diff
    };
  }

  const shell = buildEntityShellBlob(best.parsed);
  const key = businessDataStorageKey(businessId);
  if (!key) return { ok: false, error: 'invalid businessId' };

  const result = writeBusinessMergedPayload(key, shell);
  if (result && result.success === false) {
    return { ok: false, error: result.error || 'shell write failed' };
  }

  return {
    ok: true,
    businessId: String(businessId),
    key,
    payloadVersion: shell.payloadVersion,
    shell
  };
}
